import {
    createStyles,
    WithStyles,
    withStyles
} from '@mui/styles';
import { Theme } from '@mui/material/styles';
import * as React from 'react';
import {
    NodesTreeView,
    RenderTree
} from '../../shared/components/node-tree';
import RemoveIcon from '@mui/icons-material/Remove';
import { EntityFloorplan, EntityFolder } from '../../shared/domain';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const styles = (theme: Theme) => createStyles({
    treeView: {
        '& .MuiTreeItem-root.Mui-selected > .MuiTreeItem-content .MuiTreeItem-label': {
            background: 'rgba(54, 159, 198, 0.3)'
        }
    },
    iconItem: {
        transform: 'scale(0.5)'
    }
});

interface Props {
    folderTreeData?: RenderTree;
    foldersClicked?: string[];
    foldersExpanded?: string[];
    isLoading: boolean;
    onFolderTreeClick?: (folderId: string) => Promise<RenderTree[]>;
    onFolderTreeToggle?: (folderIds: string[]) => void;
    onSelectFolder?: (selectedFolder: EntityFolder) => void;
    onSelectFloorplan?: (selectedFloorplan: EntityFloorplan | undefined) => void;
}


class FolderTree extends React.Component<Props & WithStyles<typeof styles>> {
    public static defaultProps = {};

    updateFolderNode = (parent: RenderTree, nodeId: string, nodeLabel: string, updateCallback: (nodeIds: string[]) => void): void => {
        const {
            onFolderTreeClick,
            onFolderTreeToggle,
            onSelectFolder,
            foldersClicked,
            foldersExpanded,
            folderTreeData
        } = this.props;

        if (!folderTreeData) {
            return;
        }

        const isFirstChild = !foldersClicked?.length;
        const foldersClickedList = foldersClicked ? [...foldersClicked, folderTreeData.id] : [folderTreeData.id];
        const foldersExpandedList = foldersExpanded?.length ? foldersExpanded : foldersClickedList;
        // Check if node is floorplan by prefix
        const floorplanPrefix = 'floorplan::';
        if (onFolderTreeClick && onFolderTreeToggle && onSelectFolder) {
            // Check if is the first child to update the parent directly
            if (isFirstChild) {
                onFolderTreeClick(nodeId).then((result) => {
                    parent.children = result;
                    // Update and expand the new folders list
                    const newExpandedFolderList = [...foldersExpandedList];
                    updateCallback(newExpandedFolderList);
                    onFolderTreeToggle(newExpandedFolderList);
                });
            } else {
                parent.children?.map((item: RenderTree) => {
                    // Loop into list and update the children that match
                    if (item.id === nodeId) {
                        onFolderTreeClick(nodeId).then((result) => {
                            // Get floorplans from folder
                            const floorplans = result.filter((item) => {
                                const { id } = item;
                                return id.includes(floorplanPrefix);
                            });
                            // Check if folder has floorplans
                            if (floorplans.length) {
                                // Trigger and send the selected folder
                                const selectedFolder = {
                                    folder_id: nodeId,
                                    folder_name: nodeLabel
                                };

                                onSelectFolder(selectedFolder);
                            } else {
                                // Update the children and expand the new folders list
                                item.children = result;
                                const newExpandedFolderList = [...foldersExpandedList, nodeId];
                                updateCallback(newExpandedFolderList);
                                onFolderTreeToggle(newExpandedFolderList);
                            }
                        });
                    // eslint-disable-next-line no-prototype-builtins
                    } else if (item.hasOwnProperty('children')) {
                        // If no matches recursive into next children
                        this.updateFolderNode(item, nodeId, nodeLabel, updateCallback);
                    }
                });
            }
        }
    };

    updateFloorplanNode = (parent: RenderTree, nodeId: string, nodeLabel: string, updateCallback: (nodeIds: string[]) => void): void => {
        const {
            onFolderTreeClick,
            onFolderTreeToggle,
            onSelectFloorplan,
            foldersClicked,
            foldersExpanded,
            folderTreeData
        } = this.props;

        if (!folderTreeData) {
            return;
        }

        const isFirstChild = !foldersClicked?.length;
        const foldersClickedList = foldersClicked ? [...foldersClicked, folderTreeData.id] : [folderTreeData.id];
        const isUpdated = foldersClickedList.filter((id) => id === nodeId).length;
        const foldersExpandedList = foldersExpanded?.length ? foldersExpanded : foldersClickedList;

        if (isFirstChild || !isUpdated) {
            // Check if node is floorplan by prefix
            const floorplanPrefix = 'floorplan::';
            if (nodeId.includes(floorplanPrefix) && onSelectFloorplan) {
                const selectedFloorplan = {
                    floorplan_id: nodeId.replace(floorplanPrefix, ''),
                    name: nodeLabel
                } as EntityFloorplan;
                onSelectFloorplan(selectedFloorplan);
            } else if (onFolderTreeClick && onFolderTreeToggle) {
                // Check if is the first child to update the parent directly
                if (isFirstChild) {
                    onFolderTreeClick(nodeId).then((result) => {
                        parent.children = result;
                        // Update and expand the new folders list
                        const newExpandedFolderList = [...foldersExpandedList];
                        updateCallback(newExpandedFolderList);
                        onFolderTreeToggle(newExpandedFolderList);
                    });
                } else {
                    parent.children?.map((item: RenderTree) => {
                        // Loop into list and update the children that match
                        if (item.id === nodeId) {
                            onFolderTreeClick(nodeId).then((result) => {
                                item.children = result;
                                // Update and expand the new folders list
                                const newExpandedFolderList = [...foldersExpandedList, nodeId];
                                updateCallback(newExpandedFolderList);
                                onFolderTreeToggle(newExpandedFolderList);
                            });
                        // eslint-disable-next-line no-prototype-builtins
                        } else if (item.hasOwnProperty('children')) {
                            // If no matches recursive into next children
                            this.updateFloorplanNode(item, nodeId, nodeLabel, updateCallback);
                        }
                    });
                }
            }
        }
    };

    showIconCondition = (nodeId: string) => {
        return nodeId.includes('floorplan');
    };

    public render() {
        const {
            classes,
            isLoading,
            folderTreeData,
            foldersExpanded,
            onSelectFloorplan,
            onFolderTreeToggle
        } = this.props;

        const nodesTreeProps = onSelectFloorplan ? {
            onNodeSelected: this.updateFloorplanNode,
            labelIconCondition: this.showIconCondition
        } : {
            onNodeSelected: this.updateFolderNode
        };

        return (
            <>
                {folderTreeData ?
                    <NodesTreeView
                        className={classes.treeView}
                        treeData={folderTreeData}
                        isLoading={isLoading}
                        labelIcon={<RemoveIcon className={classes.iconItem}/>}
                        nodesExpanded={foldersExpanded}
                        onNodeToggled={onFolderTreeToggle}
                        {...nodesTreeProps}
                    />
                    :
                    'WARNING: There is no folders associated with this account'
                }
            </>
        );
    }
}

const MUIComponent = withStyles(styles)(FolderTree);
export {MUIComponent as FolderTree};
